<script setup lang="ts">
import { computed, type CSSProperties } from 'vue';

interface Props {
  modelValue: string;
  placeholder?: string;
  disabled?: boolean;
  minRows?: number;
  maxHeight?: string;
  error?: string | null;
}

const props = withDefaults(defineProps<Props>(), {
  placeholder: '',
  disabled: false,
  minRows: 3,
  maxHeight: '50vh',
  error: null,
});

const emit = defineEmits<{
  'update:modelValue': [value: string];
}>();

const onInput = (event: Event) => {
  emit('update:modelValue', (event.target as HTMLTextAreaElement).value);
};

/**
 * При `field-sizing: content` браузер игнорирует `rows` и схлопывает пустое
 * поле до одной строки, поэтому минимум строк держит `min-height`
 * (`lh` — высота строки поля, `1rem + 2px` — вертикальные паддинги и бордер).
 * `rows` остаётся запасным вариантом для движков без `field-sizing`.
 */
const inlineStyle = computed<CSSProperties>(() => ({
  fieldSizing: 'content',
  maxHeight: props.maxHeight,
  minHeight: `calc(${props.minRows}lh + 1rem + 2px)`,
  overflowY: 'auto',
  resize: 'none',
}));
</script>

<template>
  <textarea
    :value="modelValue"
    :rows="minRows"
    :disabled="disabled"
    :placeholder="placeholder"
    :style="inlineStyle"
    class="w-full rounded-lg border border-(--base-border-color) bg-(--base-element-color) px-3 py-2 text-sm text-(--base-text-color) outline-none placeholder:text-(--base-popup-secondary-color) focus:border-(--base-accent-color) disabled:opacity-60"
    :class="{ 'border-(--base-error-color)!': !!error }"
    @input="onInput"
  ></textarea>
</template>
